import { useState } from "react";
import { Link } from "react-router-dom";
import {
  BookOpen,
  Plus,
  ShieldCheck,
  Lock,
  EyeOff,
  CalendarClock,
  Receipt,
  Wallet,
  RefreshCw,
} from "lucide-react";
import AppStoreBadges from "@/components/brand/AppStoreBadges";
import { PRIVACY_DELETION_SECTION } from "@/content/privacy.meta";
import MarketingLayout from "@/components/layout/MarketingLayout";
import { HowItWorksButton } from "@/components/walkthrough/HowItWorksButton";
import { keepUnitTogether } from "@/lib/marketingNav";
import { WALKTHROUGH_SEE_HOW_COPY } from "@fomofi/shared";
import {
  HERO_BLUE,
  BILLING_CYCLES,
  DEFAULT_CYCLE,
  SECURITY_PILLARS,
  MARKETING_IMAGE_SIZES,
  ANNUAL_SAVING_COPY,
  type BillingCycle,
} from "@/lib/marketing";
import { TRIAL_DAYS, trialLength } from "@/lib/trial";

const PHONE_HERO_SRC = "/screenshots/phone-hero.png";

// Anthony's doc §2 — four refined homepage feature cards. Copy is verbatim;
// icons are the closest lucide matches to the doc's suggested directions.
const features = [
  {
    Icon: CalendarClock,
    title: "Paycheck-Aware Forecasting",
    description: "See your spending forecast based on when you actually get paid.",
  },
  {
    Icon: Receipt,
    title: "Upcoming Bills & Subscriptions",
    description:
      "Know what bills, subscriptions, and recurring charges are coming before they hit.",
  },
  {
    Icon: Wallet,
    title: "Safe-to-Spend Number",
    description: "See what you can safely spend after upcoming expenses are accounted for.",
  },
  {
    Icon: RefreshCw,
    title: "Real-Time Updates",
    description: "Your forecast adjusts as your spending, bills, and balance change.",
  },
];

/** Decorative speckles for the hero background, in the SVG's own 0-100 user
 *  space. A module constant: it is fixed art, so it neither changes nor is
 *  rebuilt on every render, and the index each circle is keyed by is the
 *  position it draws at. */
const NORTHERN_LIGHTS_DOTS = [
  [85, 8, 1.5],
  [80, 12, 1],
  [90, 14, 1.2],
  [72, 18, 1],
  [88, 22, 1.8],
  [78, 25, 1],
  [92, 28, 1.3],
  [68, 30, 0.9],
  [84, 32, 1.5],
  [76, 35, 1.1],
  [95, 38, 1],
  [70, 40, 1.4],
  [82, 42, 1.2],
  [87, 45, 0.9],
  [74, 48, 1.6],
  [91, 50, 1.1],
  [66, 52, 1],
  [79, 55, 1.3],
  [85, 58, 0.9],
  [72, 62, 1.5],
  [89, 65, 1],
  [77, 68, 1.2],
  [94, 70, 0.9],
  [68, 72, 1.4],
  [83, 75, 1.1],
  [75, 78, 1.3],
  [90, 80, 1],
  [86, 85, 1.2],
  [71, 88, 0.9],
  [80, 92, 1.4],
  [93, 95, 1],
  [65, 45, 1.1],
  [55, 60, 0.9],
  [60, 25, 1.3],
  [50, 70, 1],
] as const;

function NorthernLightsPattern() {
  return (
    <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
      <svg
        viewBox="0 0 100 100"
        preserveAspectRatio="xMaxYMid slice"
        className="absolute inset-0 h-full w-full"
      >
        <defs>
          <radialGradient id="glow-1" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#D9E7F5" stopOpacity="0.85" />
            <stop offset="70%" stopColor="#E8F0F9" stopOpacity="0.45" />
            <stop offset="100%" stopColor="#F6F9FC" stopOpacity="0" />
          </radialGradient>
          <radialGradient id="glow-2" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#DCE3F4" stopOpacity="0.7" />
            <stop offset="100%" stopColor="#F0F3FA" stopOpacity="0" />
          </radialGradient>
          <linearGradient id="wave-stroke" x1="0" x2="1" y1="0" y2="0">
            <stop offset="0%" stopColor="#BFD2E8" stopOpacity="0" />
            <stop offset="50%" stopColor="#A9C2DE" stopOpacity="0.5" />
            <stop offset="100%" stopColor="#BFD2E8" stopOpacity="0" />
          </linearGradient>
        </defs>

        {/* Soft glowing ellipses — clustered right */}
        <ellipse cx="85" cy="35" rx="38" ry="32" fill="url(#glow-1)" />
        <ellipse cx="92" cy="65" rx="30" ry="28" fill="url(#glow-2)" />
        <ellipse cx="70" cy="75" rx="22" ry="18" fill="url(#glow-1)" opacity="0.6" />
        <ellipse cx="78" cy="15" rx="18" ry="14" fill="url(#glow-2)" opacity="0.55" />

        {/* Wavy northern-lights ribbons */}
        <path
          d="M 40 20 Q 60 10, 80 25 T 100 40"
          stroke="url(#wave-stroke)"
          strokeWidth="0.6"
          fill="none"
          opacity="0.7"
        />
        <path
          d="M 45 40 Q 65 30, 82 48 T 100 65"
          stroke="url(#wave-stroke)"
          strokeWidth="0.5"
          fill="none"
          opacity="0.6"
        />
        <path
          d="M 50 65 Q 70 55, 88 72 T 100 85"
          stroke="url(#wave-stroke)"
          strokeWidth="0.5"
          fill="none"
          opacity="0.6"
        />
        <path
          d="M 42 85 Q 62 78, 82 92"
          stroke="url(#wave-stroke)"
          strokeWidth="0.4"
          fill="none"
          opacity="0.5"
        />

        {/* Scattered dot speckles — light & subtle */}
        {NORTHERN_LIGHTS_DOTS.map(([cx, cy, r], i) => (
          <circle key={i} cx={cx} cy={cy} r={r * 0.18} fill="#D7E1EC" opacity={0.6} />
        ))}
      </svg>
    </div>
  );
}

function PhoneMockup() {
  return (
    <img
      src={PHONE_HERO_SRC}
      alt="Fomo-Fi dashboard showing recurring bills covered, upcoming bills, and remaining to spend"
      className="relative z-10 h-auto w-full max-w-[460px] drop-shadow-2xl"
      loading="eager"
      {...MARKETING_IMAGE_SIZES[PHONE_HERO_SRC]}
    />
  );
}

/**
 * Band tints alternate strictly, and the run ENDS on a tint because the footer
 * is white: hero(white) → security(tint) → pricing(white) → faq(tint) → footer.
 *
 * This is load-bearing, not decoration. Before BEN-141 item 9 the run was
 * hero → features(tint) → security(white) → pricing(tint) → faq(white) →
 * FinalCta(gradient) → footer(white), and the gradient was the only thing
 * keeping a white FAQ off a white footer. Deleting the features band and the
 * FinalCta (Anthony: "we have a features page" / "why is this here?") would
 * have left TWO white-on-white seams — hero against security at the top and faq
 * against the footer at the bottom — so the three surviving bands were re-tinted
 * rather than left as they fell.
 *
 * If you add or remove a band, re-check the whole run: no two adjacent surfaces
 * may share a tint, and the last band must not be white. Guarded by
 * marketingRouteIntegrity.test.tsx.
 */
export default function Landing() {
  return (
    <MarketingLayout>
      {/* Hero */}
      <section className="relative overflow-hidden">
        <NorthernLightsPattern />

        <div className="relative mx-auto grid max-w-[1440px] grid-cols-[minmax(0,1fr)] items-start gap-8 px-5 pb-8 pt-32 sm:px-10 lg:grid-cols-[1.1fr_1fr] lg:gap-10 lg:px-16 lg:pb-12 lg:pt-36">
          {/* Left column */}
          <div>
            <h1
              className="font-extrabold tracking-tight text-neutral-900 dark:text-foreground"
              style={{
                // The floor is min(2.5rem, 12vw): 40px at every phone width at default text (12vw
                // is 43px at 360), but at 200% text it stops "spending" (80px, ~350px wide)
                // from pushing the hero column past a 360 screen.
                fontSize: "clamp(min(2.5rem, 12vw), 4.6vw, 4rem)",
                lineHeight: 1.08,
                letterSpacing: "-0.025em",
              }}
            >
              {/* nowrap only from sm: at 40px the line measures 439px, so below
                  640 it forced the grid column past the viewport and the
                  section's overflow-hidden clipped the hero — headline, subcopy
                  and CTA caption all lost their right edge. Wrapping below sm
                  lets the column collapse to the viewport. */}
              <span className="block sm:whitespace-nowrap">
                Forecast your <span style={{ color: HERO_BLUE }}>spending</span>
              </span>
              <span className="block">
                before <span style={{ color: HERO_BLUE }}>payday</span> hits.
              </span>
            </h1>
            <p className="mt-5 max-w-[440px] text-[17px] leading-[1.55] text-neutral-600 dark:text-muted-foreground">
              Know what you can safely spend, based on your paycheck, bills, and real-life habits.
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link
                to="/auth/signup"
                className="inline-flex items-center rounded-lg px-8 py-3.5 text-lg font-semibold text-white shadow-md transition hover:opacity-90"
                style={{ backgroundColor: HERO_BLUE }}
              >
                Start 7-Day Free Trial
              </Link>
              {/* Doc 3 B4/B5 (Anthony's package, 2026-10-28): a second button beside the
                  trial, outlined with a book icon, opening the walkthrough at step 1. */}
              <HowItWorksButton className="inline-flex min-h-11 max-w-full items-center gap-2 rounded-lg border-2 border-[#2196F3] bg-white px-6 py-3 text-lg font-semibold transition hover:bg-neutral-50 dark:bg-background dark:hover:bg-muted">
                <BookOpen className="h-5 w-5 shrink-0" aria-hidden style={{ color: HERO_BLUE }} />
                <span className="min-w-0" style={{ color: HERO_BLUE }}>
                  {keepUnitTogether(WALKTHROUGH_SEE_HOW_COPY)}
                </span>
              </HowItWorksButton>
            </div>
            {/* Caption follows the button. It read "Create your free account and
                start forecasting in minutes." while the button said "Create
                Account"; with the button now selling the trial, the account
                framing is the wrong half of the sentence to keep. Cancel-anytime
                replaces it (Anthony) — and deliberately without another "7 day",
                since he wants those thinned out, not multiplied. */}
            <p className="mt-3 text-[15px] text-neutral-700 dark:text-muted-foreground">
              Start forecasting in minutes. Cancel anytime.
            </p>

            {/* Store badges are non-interactive until the native apps publish
                — see AppStoreBadges for why there's no href. */}
            <AppStoreBadges className="mt-6" />

            {/* §2 four refined feature cards + section headline — above the fold */}
            <div className="mt-12">
              <h2
                className="font-extrabold tracking-tight text-neutral-900 dark:text-foreground"
                style={{ fontSize: "clamp(1.4rem, 2.2vw, 1.875rem)", letterSpacing: "-0.02em" }}
              >
                Everything you need to know before payday.
              </h2>
              <p className="mt-2 max-w-[460px] text-[15px] leading-snug text-neutral-600 dark:text-muted-foreground">
                Fomo-Fi combines paycheck timing, bills, subscriptions, and spending activity to
                show what&rsquo;s safe to spend.
              </p>

              <div className="mt-8 grid grid-cols-1 gap-x-10 gap-y-8 sm:grid-cols-2">
                {features.map(({ Icon, title, description }) => (
                  <div key={title} className="flex items-start gap-4">
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-[#EEF4FB] dark:bg-muted/40">
                      <Icon className="h-6 w-6" strokeWidth={2} style={{ color: HERO_BLUE }} />
                    </div>
                    <div className="pt-0.5">
                      <h3 className="text-lg font-bold tracking-tight text-neutral-900 dark:text-foreground">
                        {title}
                      </h3>
                      <p className="mt-1 max-w-[260px] text-[14px] leading-snug text-neutral-600 dark:text-muted-foreground">
                        {description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right column: phone */}
          <div className="relative flex items-start justify-center lg:justify-end">
            <PhoneMockup />
          </div>
        </div>
      </section>

      <SecuritySection />
      <PricingSection />
      <FaqSection />
    </MarketingLayout>
  );
}

/* ────────────────────────── Security ────────────────────────── */

function SecuritySection() {
  // Copy moved to lib/marketing so /features' trust strip reuses these exact
  // reviewed strings rather than a second copy that could drift (#557/#558).
  const ICONS = [Lock, ShieldCheck, EyeOff];
  const pillars = SECURITY_PILLARS.map((p, i) => ({ ...p, Icon: ICONS[i] }));
  return (
    <section
      id="security"
      className="scroll-mt-24 relative overflow-hidden bg-[#F7FAFD] py-24 dark:bg-background"
    >
      <div className="mx-auto max-w-[1440px] px-10 lg:px-16">
        <div className="mb-14 text-center">
          <p className="text-sm font-semibold uppercase tracking-[0.2em] text-sky-500">Security</p>
          <h2
            className="mt-3 font-extrabold tracking-tight text-neutral-900 dark:text-foreground"
            style={{ fontSize: "clamp(2rem, 3.8vw, 3rem)", letterSpacing: "-0.02em" }}
          >
            Built with your trust in mind.
          </h2>
        </div>

        <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
          {pillars.map(({ Icon, title, body }) => (
            <div
              key={title}
              className="rounded-2xl border border-neutral-100 bg-white p-8 text-center shadow-sm dark:bg-card dark:border-border"
            >
              <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-[#EEF4FB] dark:bg-muted/40">
                <Icon className="h-6 w-6" style={{ color: HERO_BLUE }} />
              </div>
              <h3 className="text-lg font-bold tracking-tight text-neutral-900 dark:text-foreground">
                {title}
              </h3>
              <p className="mt-2 text-[15px] leading-relaxed text-neutral-600 dark:text-muted-foreground">
                {body}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ────────────────────────── Pricing ────────────────────────── */

// Locked pricing. Must stay in sync with `PLANS` in src/mocks/mockPayment.ts
// (payment page) and the FAQ answer in src/pages/Help.tsx — the three are the
// only places these numbers appear.
//   12 × $11.99 = $143.88 vs $119.99/yr → saves $23.89 (16.60% → "Save 17%").
// The badge copy itself lives in BILLING_CYCLES.annual.saveLabel; see the note
// there for why this is a percentage now and not "2 months free".
// Key order drives the segmented control's left-to-right order.
function PricingSection() {
  const [cycle, setCycle] = useState<BillingCycle>(DEFAULT_CYCLE);
  const plan = BILLING_CYCLES[cycle];

  const includes = [
    "Unlimited bank connections",
    "Cash flow forecasting up to 12 months",
    "Bill & paycheck calendar",
    "Snowball + avalanche debt payoff",
    "Real-time spending alerts",
    "iOS, Android, and web, one account",
  ];
  return (
    <section
      id="pricing"
      className="scroll-mt-24 relative overflow-hidden bg-white py-24 dark:bg-card"
    >
      <div className="mx-auto max-w-[1440px] px-10 lg:px-16">
        <div className="mb-14 text-center">
          <p className="text-sm font-semibold uppercase tracking-[0.2em] text-sky-500">Pricing</p>
          <h2
            className="mt-3 font-extrabold tracking-tight text-neutral-900 dark:text-foreground"
            style={{ fontSize: "clamp(2rem, 3.8vw, 3rem)", letterSpacing: "-0.02em" }}
          >
            Simple pricing. Start with a free trial.
          </h2>
          <p className="mx-auto mt-4 max-w-[520px] text-[17px] leading-relaxed text-neutral-600 dark:text-muted-foreground">
            Try Fomo-Fi free for {TRIAL_DAYS} days. Pick monthly or annual when you start; nothing
            is charged until your trial ends.
          </p>
        </div>

        {/* Monthly / Annual segmented control. `role="radiogroup"` + aria-checked
            mirrors the plan picker on the payment page (PlanCard). */}
        <div className="mb-8 flex flex-col items-center gap-3 sm:flex-row sm:justify-center">
          <div
            role="radiogroup"
            aria-label="Billing period"
            className="inline-flex max-w-full flex-wrap items-center justify-center rounded-full border border-[#E4EEF7] bg-white p-1 shadow-sm dark:bg-card dark:border-border"
          >
            {(Object.keys(BILLING_CYCLES) as BillingCycle[]).map((id) => {
              const active = id === cycle;
              return (
                <button
                  key={id}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  onClick={() => setCycle(id)}
                  className="rounded-full px-6 py-2.5 text-[15px] font-semibold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:ring-offset-2"
                  style={
                    active
                      ? { backgroundColor: HERO_BLUE, color: "#FFFFFF" }
                      : { color: "var(--mk-nav)" }
                  }
                >
                  {BILLING_CYCLES[id].label}
                </button>
              );
            })}
          </div>

          {plan.saveLabel && (
            <span
              className="inline-flex items-center rounded-full px-4 py-1.5 text-sm font-bold"
              style={{ backgroundColor: "#22B97A", color: "#FFFFFF" }}
            >
              {plan.saveLabel}
            </span>
          )}
        </div>

        <div className="mx-auto max-w-[520px]">
          <div
            // AN-51: the gradient and border as classes, so Dark replaces them; an inline
            // backgroundImage sat over dark:bg-card and left light text on a white card.
            className="relative overflow-hidden rounded-3xl border border-[#E4EEF7] bg-linear-to-b from-white to-[#F6FAFE] p-10 shadow-xl dark:border-border dark:from-card dark:to-card"
          >
            <div className="text-center">
              <p className="text-sm font-semibold uppercase tracking-[0.18em] text-sky-500">
                One membership
              </p>
              <p
                aria-live="polite"
                className="mt-4 text-5xl font-extrabold tracking-tight text-neutral-900 dark:text-foreground"
              >
                {plan.price}
                <span className="text-lg font-semibold text-neutral-500 dark:text-muted-foreground">
                  {plan.unit}
                </span>
              </p>
              <p className="mt-2 text-[15px] font-medium text-neutral-700 dark:text-muted-foreground">
                {plan.detail}
              </p>
              {/*
                BEN-141, Vinnie item 3: the annual card had "no savings framing".
                `saveLabel` already carried the percentage, but it renders beside the
                billing toggle a row above — apart from the price it qualifies —
                so the card on its own carried no framing at all. Stated here,
                against the price, from `ANNUAL_SAVING_COPY` (AN-04).

                Derived, never written down: the 2026-09-24 re-price (on hold)
                moves the saving from 17% to 11%, and a literal would survive
                that change while silently ceasing to be true.
              */}
              {cycle === "annual" && (
                <p className="mt-1 text-[15px] font-semibold" style={{ color: "#189369" }}>
                  {ANNUAL_SAVING_COPY}
                </p>
              )}
            </div>

            <p className="mt-8 text-xs font-semibold uppercase tracking-[0.15em] text-neutral-500 dark:text-muted-foreground">
              Every plan includes
            </p>
            <ul className="mt-3 space-y-3">
              {includes.map((item) => (
                <li
                  key={item}
                  className="flex items-center gap-3 text-[15px] text-neutral-800 dark:text-foreground"
                >
                  <span
                    className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full"
                    style={{ backgroundColor: "#E7F5EF", color: "#22B97A" }}
                  >
                    <svg viewBox="0 0 24 24" className="h-3 w-3" fill="none" aria-hidden="true">
                      <path
                        d="M5 13l4 4L19 7"
                        stroke="currentColor"
                        strokeWidth="3"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </span>
                  {item}
                </li>
              ))}
            </ul>

            {/* Display-only: routes to signup. There is no checkout to send
                people to until the Stripe subscription flow lands. */}
            <Link
              to="/auth/signup"
              className="mt-8 inline-flex w-full items-center justify-center rounded-lg px-8 py-4 text-lg font-semibold text-white shadow-md transition hover:opacity-90"
              style={{ backgroundColor: HERO_BLUE }}
            >
              Start 7-Day Trial
            </Link>
            <p className="mt-3 text-center text-[13px] text-neutral-500 dark:text-muted-foreground">
              {plan.trialFooter}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ────────────────────────── FAQ ────────────────────────── */

// FAQ — copy is verbatim from Anthony's QA #14 set. Three deliberate
// departures, all flagged back to him:
//   Q1  his answer opened with "Yes." to a "Where" question — dropped.
//   Q6  his text trailed off into a note-to-self ("Users should also be told
//       whether any features are available without connecting an account").
//       That's direction, not copy, so it isn't pasted. The clause we do add
//       states only what the code guarantees: onboarding has a real "Skip for
//       now" path (Onboarding.handleSkip) that lands you on the dashboard
//       unconnected. WHICH features are useful in that state is a product
//       call — Anthony still owes us that list.
//   Q8  his text ended "Include links to the Privacy Policy and data-deletion
//       process." — an instruction, so we link the real pages instead.
//       The deletion link targets the privacy section whose heading names
//       deletion (PRIVACY_DELETION_SECTION, BEN-141 L2: a hard-coded number went
//       stale when counsel renumbered the policy);
//       LegalLayout honours a hash deep-link before its scroll-to-top (#634),
//       so that anchor genuinely lands. There is no standalone public
//       deletion request page to point at — see the PR notes.
const faqs: { q: string; a: React.ReactNode }[] = [
  {
    q: "Where can I use Fomo-Fi?",
    a: "Fomo-Fi is available on the web, iPhone & Android.",
  },
  {
    q: "What happens after I sign up?",
    a: "After creating your account, you will connect to your financial institutions, review the income and recurring bills Fomo-Fi identifies, confirm your next paycheck, and receive your personalized forecast and recommended budget buckets.",
  },
  {
    q: `How does the ${trialLength()} free trial work?`,
    a: `Your free trial begins when you activate your Fomo-Fi subscription and includes full access to the app for ${TRIAL_DAYS} days. Before confirming, you'll see the subscription price and the date your paid plan will begin. You may cancel anytime during the trial to avoid being charged. If you do not cancel, your selected subscription will automatically renew after the trial ends.`,
  },
  {
    q: "Can I choose monthly or annual billing?",
    a: "Yes. You can choose either monthly or annual billing when you start your Fomo-Fi subscription. Annual billing may offer a lower overall cost, while monthly billing provides more flexibility. Your selected price and renewal schedule will be shown before you confirm your subscription.",
  },
  {
    q: "How does Fomo-Fi create my financial forecast?",
    a: "Fomo-Fi analyzes your current balance, recurring income, upcoming bills, paycheck schedule, and spending history. It then estimates what will remain before your next paycheck and projects your future account balance.",
  },
  {
    // BEN-141, Vinnie item 4. He asked for an answer stating that Fomo-Fi
    // "analyzes 12 months of bank transactions". That claim is NOT shipped as
    // written, because we cannot evidence it:
    //
    //   `banking/services.py` sends `LinkTokenTransactions(days_requested=365)`.
    //   `days_requested` is a REQUEST. Plaid's returned depth is per-institution
    //   and best-effort — its own default without that parameter is 90 days, and
    //   the sandbox we develop against returns a 14-day fixture window
    //   (`SANDBOX_FIXTURE_HISTORY_DAYS`). No user has been observed receiving a
    //   full year.
    //
    // So "analyzes 12 months" would assert a guarantee that depends on a third
    // party, on a public marketing page, about a financial product. Same class as
    // the "AES-256" encryption claim and the response-time guarantee removed in
    // #557/#558 — and, like those, softened rather than dropped: the 12-month
    // figure is still the headline, correctly attributed as what we ASK for.
    q: "How much of my transaction history does Fomo-Fi analyze?",
    a: "When you connect an account, Fomo-Fi requests up to 12 months of transaction history. How much is actually available depends on your bank: some share the full year, others share less. Fomo-Fi works with whatever your bank provides, and your forecast and categories keep improving as new transactions arrive.",
  },
  {
    q: "Do I need to connect a bank account?",
    a: "A bank connection is needed for automatic transaction analysis, income detection, recurring-bill identification, and forecasting. You can skip this step during setup and connect an account later.",
  },
  {
    q: "Can Fomo-Fi move or withdraw my money?",
    a: "No. Fomo-Fi uses a read-only bank connection and cannot transfer funds, withdraw money, make purchases, or change your bank account. You can disconnect a financial institution from your settings at any time.",
  },
  {
    q: "How is my financial information protected and used?",
    a: (
      <>
        Fomo-Fi uses your transaction and account information to categorize spending, identify
        income and recurring bills, create forecasts, and recommend allocations. Your information is
        not sold. Read our{" "}
        <Link to="/privacy" className="font-medium text-sky-600 underline underline-offset-2">
          Privacy Policy
        </Link>
        , or see{" "}
        <Link
          to={PRIVACY_DELETION_SECTION ? `/privacy#${PRIVACY_DELETION_SECTION}` : "/privacy"}
          className="font-medium text-sky-600 underline underline-offset-2"
        >
          how to delete your data
        </Link>
        .
      </>
    ),
  },
];

function FaqSection() {
  return (
    <section
      id="faq"
      className="scroll-mt-24 relative overflow-hidden bg-[#F7FAFD] py-24 dark:bg-background"
    >
      <div className="mx-auto max-w-[1440px] px-10 lg:px-16">
        <div className="mx-auto max-w-[860px]">
          <div className="mb-14 text-center">
            <p className="text-sm font-semibold uppercase tracking-[0.2em] text-sky-500">FAQ</p>
            <h2
              className="mt-3 font-extrabold tracking-tight text-neutral-900 dark:text-foreground"
              style={{ fontSize: "clamp(2rem, 3.8vw, 3rem)", letterSpacing: "-0.02em" }}
            >
              Questions? We&rsquo;ve got answers.
            </h2>
          </div>

          <div className="space-y-3">
            {faqs.map((f) => (
              <details
                key={f.q}
                className="group rounded-xl border border-neutral-200 bg-white p-5 open:border-sky-200 open:shadow-sm dark:bg-card dark:border-border"
              >
                <summary className="flex cursor-pointer items-center justify-between text-left text-[17px] font-semibold text-neutral-900 [&::-webkit-details-marker]:hidden dark:text-foreground">
                  {f.q}
                  <Plus
                    className="h-5 w-5 shrink-0 text-neutral-400 transition-transform group-open:rotate-45 dark:text-muted-foreground"
                    strokeWidth={2}
                  />
                </summary>
                <p className="mt-3 text-[15px] leading-relaxed text-neutral-600 dark:text-muted-foreground">
                  {f.a}
                </p>
              </details>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
